import { Component, ElementRef, Input, OnChanges, OnInit, SimpleChanges, ViewChild, HostListener, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import * as d3 from 'd3';

export interface IFunnelDataPoint {
    label: string;
    value: string | number;
    percentage: number;
    color?: string;
    amount?: string | number;
    leftLabel?: string;
    rightLabel?: string;
    legend?: string;
}

export interface IFunnelConfig {
    title?: string;
    subtitle?: string;
    animate?: boolean;
    barStyle?: 'solid' | 'gradient';
}

@Component({
    selector: 'app-funnel-chart',
    standalone: true,
    imports: [CommonModule],
    templateUrl: './funnel-chart.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./funnel-chart.component.scss']
})
export class FunnelChartComponent implements OnInit, OnChanges {
    @Input() data: IFunnelDataPoint[] = [];
    @Input() config: IFunnelConfig = { title: 'Funnel Analysis' };

    // Custom Dimensions
    @Input() width: string = '100%';
    @Input() height: string = '100%';
    @Input() min_height: string = '220px';
    @Input() showLegends: boolean = true;

    @Input() margins?: { top: number, right: number, bottom: number, left: number };
    @Input() showBorder: boolean = true;
    @Input() showShadow: boolean = true;
    @Input() padding: string = '20px';
    @Input() darkThemeClass: string = 'dark:bg-surface-1';

    // New Feature: Label Visibility
    @Input() showLabels: boolean = true;
    @Input() showLeftLabel: boolean = true;
    @Input() showRightLabel: boolean = true;
    @Input() showLegendColor: boolean = false;
    @Input() showCenterLabels: boolean = true;
    @Input() showPercentage: boolean = true;

    // Allows removing interlocking gaps and rounded corners for a straight sharp funnel
    @Input() isContinuous: boolean = false;
    @Input() funnelShape: 'cone' | 'pipeline' = 'cone';

    // Aligns labels far right with a long connecting dotted line
    @Input() rightAlignLabels: boolean = false;

    @ViewChild('chartContainer', { static: true }) chartContainer!: ElementRef;

    private resizeObserver: ResizeObserver | null = null;

    constructor() { }

    ngOnInit(): void {
        this.setupResizeObserver();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if ((changes['data'] || changes['width'] || changes['height'] || changes['margins']) && this.chartContainer) {
            this.render();
        }
    }

    private setupResizeObserver(): void {
        this.resizeObserver = new ResizeObserver(() => {
            this.render();
        });
        this.resizeObserver.observe(this.chartContainer.nativeElement);
    }

    private render(): void {
        const container = this.chartContainer.nativeElement;
        d3.select(container).selectAll('*').remove();

        const rect = container.getBoundingClientRect();
        const width = rect.width || container.clientWidth || 600;
        const height = rect.height || container.clientHeight || 400;

        if (width < 50 || height < 50) return;

        // Responsive margins - smaller on mobile, larger on desktop
        const isMobile = width < 640;

        // Use provided margins or defaults
        const margin = this.margins || {
            top: 20,
            right: isMobile ? 40 : 60,
            bottom: 20,
            left: isMobile ? 40 : 60
        };
        const chartWidth = Math.max(0, width - margin.left - margin.right);
        const chartHeight = Math.max(0, height - margin.top - margin.bottom);

        const svg = d3.select(container)
            .append('svg')
            .attr('width', '100%')
            .attr('height', '100%')
            .attr('viewBox', `0 0 ${width} ${height}`)
            .attr('preserveAspectRatio', 'xMidYMid meet')
            .append('g')
            .attr('transform', `translate(${margin.left}, ${margin.top})`);

        const layerCount = this.data.length;
        const layerHeight = chartHeight / layerCount;

        // Funnel calculations - Cone style taper
        const topWidth = chartWidth;
        const bottomWidth = this.funnelShape === 'pipeline' ? chartWidth * 0.35 : (this.isContinuous ? chartWidth * 0.45 : chartWidth * 0.25);

        const getXPos = (layerIndex: number, isBottom: boolean) => {
            if (this.funnelShape === 'pipeline') {
                const limit = Math.max(1, layerCount - 2);
                const effectiveLayer = Math.min(layerIndex + (isBottom ? 1 : 0), limit);
                const progress = effectiveLayer / limit;
                const currentWidth = topWidth - (topWidth - bottomWidth) * progress;
                return (topWidth - currentWidth) / 2;
            }
            const progress = (layerIndex + (isBottom ? 1 : 0)) / layerCount;
            const currentWidth = topWidth - (topWidth - bottomWidth) * progress;
            return (topWidth - currentWidth) / 2;
        };

        const getWidth = (layerIndex: number, isBottom: boolean) => {
            if (this.funnelShape === 'pipeline') {
                const limit = Math.max(1, layerCount - 2);
                const effectiveLayer = Math.min(layerIndex + (isBottom ? 1 : 0), limit);
                const progress = effectiveLayer / limit;
                return topWidth - (topWidth - bottomWidth) * progress;
            }
            const progress = (layerIndex + (isBottom ? 1 : 0)) / layerCount;
            return topWidth - (topWidth - bottomWidth) * progress;
        };

        // Filters
        const defs = svg.append('defs');

        // Shadow filter
        const shadow = defs.append('filter')
            .attr('id', 'shadow')
            .attr('x', '-20%')
            .attr('y', '-20%')
            .attr('width', '140%')
            .attr('height', '140%');
        shadow.append('feDropShadow')
            .attr('dx', 0)
            .attr('dy', 2)
            .attr('stdDeviation', 2.5)
            .attr('flood-opacity', 0.15);

        // Generate gradients if enabled
        if (this.config.barStyle === 'gradient') {
            this.data.forEach((d, i) => {
                const gradientId = `funnel-gradient-${i}`;
                const color = d.color || '#3b82f6';

                // Create color variations for 3D effect without transparency bleed
                const c = d3.color(color);
                const startColor = c ? c.brighter(0.8).toString() : color;
                const endColor = c ? c.darker(0.8).toString() : color;

                const gradient = defs.append('linearGradient')
                    .attr('id', gradientId)
                    .attr('x1', '0%')
                    .attr('y1', '0%')
                    .attr('x2', '0%')
                    .attr('y2', '100%');

                gradient.append('stop')
                    .attr('offset', '0%')
                    .attr('stop-color', startColor)
                    .attr('stop-opacity', 1);

                gradient.append('stop')
                    .attr('offset', '100%')
                    .attr('stop-color', endColor)
                    .attr('stop-opacity', 1);
            });
        }

        // Render from bottom to top so that top layers are appended LAST (on top in Z-index)
        [...this.data].reverse().forEach((_, reverseIndex) => {
            const i = layerCount - 1 - reverseIndex;
            const d = this.data[i];

            const yTop = i * layerHeight;
            const yBottom = (i + 1) * layerHeight;

            const xTopLeft = getXPos(i, false);
            const xTopRight = xTopLeft + getWidth(i, false);
            const xBottomLeft = getXPos(i, true);
            const xBottomRight = xBottomLeft + getWidth(i, true);
            const centerX = chartWidth / 2;

            const radius = 30; // Premium plate aesthetic
            const isLast = i === layerCount - 1;
            const tabWidth = getWidth(i, true) * 0.35;
            const tabHeight = 8;
            const tr = 6; // tab radius

            let actualYBottomRight = yBottom;
            if (this.isContinuous && this.funnelShape === 'pipeline' && isLast) {
                actualYBottomRight = yTop + layerHeight * 0.45; // Slant the bottom right up
            }

            let pathData = '';

            if (this.isContinuous) {
                pathData = `
                    M ${xTopLeft},${yTop}
                    L ${xTopRight},${yTop}
                    L ${xBottomRight},${actualYBottomRight}
                    L ${xBottomLeft},${yBottom}
                    Z
                `;
            } else {
                pathData = `
                    M ${xTopLeft + radius},${yTop}
                    L ${xTopRight - radius},${yTop}
                    Q ${xTopRight},${yTop} ${xTopRight - radius * 0.3},${yTop + radius * 0.5}
                    L ${xBottomRight},${yBottom}
                `;

                if (!isLast) {
                    pathData += `
                        L ${centerX + tabWidth / 2},${yBottom}
                        L ${centerX + tabWidth / 2},${yBottom + tabHeight - tr}
                        Q ${centerX + tabWidth / 2},${yBottom + tabHeight} ${centerX + tabWidth / 2 - tr},${yBottom + tabHeight}
                        L ${centerX - tabWidth / 2 + tr},${yBottom + tabHeight}
                        Q ${centerX - tabWidth / 2},${yBottom + tabHeight} ${centerX - tabWidth / 2},${yBottom + tabHeight - tr}
                        L ${centerX - tabWidth / 2},${yBottom}
                    `;
                }

                pathData += `
                    L ${xBottomLeft},${yBottom}
                    L ${xTopLeft + radius * 0.3},${yTop + radius * 0.5}
                    Q ${xTopLeft},${yTop} ${xTopLeft + radius},${yTop}
                    Z
                `;
            }

            const color = d.color || '#3b82f6';
            const fill = this.config.barStyle === 'gradient' ? `url(#funnel-gradient-${i})` : color;
            const centerY = (yTop + yBottom) / 2;
            const rightCenterY = (yTop + actualYBottomRight) / 2;

            // Layer Group
            const layerGroup = svg.append('g')
                .datum(i) // Bind index directly for reliable sorting
                .attr('class', 'layer-group cursor-pointer transition-all duration-300')
                .attr('id', `layer-${i}`);

            const shape = layerGroup.append('path')
                .attr('d', pathData)
                .attr('fill', fill)
                .attr('opacity', 1)
                .style('filter', this.showShadow ? 'url(#shadow)' : 'none')
                .on('mouseover', function (event) {
                    const g = d3.select(this.parentNode as any);
                    g.raise(); // Bring to immediate front for hover focus

                    // Blur others
                    svg.selectAll('.layer-group')
                        .filter(idx => idx !== i)
                        .style('filter', 'blur(3px)')
                        .style('opacity', 0.4);

                    // Scale
                    g.style('transform-origin', `${centerX}px ${centerY}px`)
                        .transition().duration(150).attr('transform', 'scale(1.06)');

                    // Text Swap
                    g.select('.inner-label')
                        .text(d.amount || d.value)
                        .style('font-size', '14px')
                        .style('fill', '#fff');
                })
                .on('mouseout', function (event) {
                    svg.selectAll('.layer-group')
                        .style('filter', null)
                        .style('opacity', 1);

                    const g = d3.select(this.parentNode as any);
                    g.transition().duration(150).attr('transform', 'scale(1)');

                    g.select('.inner-label')
                        .text(d.label)
                        .style('font-size', '10px')
                        .style('fill', '#fff');

                    // Restore original interlocking order:
                    // bIdx - aIdx descending results in [3, 2, 1, 0] in DOM
                    // index 0 is the last child, so it sits on TOP.
                    svg.selectAll('.layer-group')
                        .sort((aIdx: any, bIdx: any) => bIdx - aIdx);
                });

            if (this.config.animate) {
                shape.attr('transform', 'scale(0.95)')
                    .attr('opacity', 0)
                    .transition()
                    .delay(i * 100)
                    .duration(800)
                    .ease(d3.easeElasticOut)
                    .attr('transform', 'scale(1)')
                    .attr('opacity', 1);
            }

            if (this.showLabels) {
                // Connection Lines - responsive positioning
                const lineOffset = isMobile ? -8 : -12;
                const lineEndOffset = isMobile ? 6 : 8;

                if (this.showLeftLabel) {
                    svg.append('line')
                        .attr('x1', lineOffset)
                        .attr('y1', centerY)
                        .attr('x2', xTopLeft - lineEndOffset)
                        .attr('y2', centerY)
                        .attr('stroke', '#94a3b8')
                        .attr('stroke-dasharray', '3,3')
                        .attr('opacity', 0.5);

                    // Left % - responsive positioning
                    const leftX = isMobile ? -12 : -16;
                    svg.append('text')
                        .attr('x', leftX)
                        .attr('y', centerY)
                        .attr('text-anchor', 'end')
                        .attr('dominant-baseline', 'middle')
                        .attr('class', 'text-[11px] font-medium text-slate-500 dark:text-gray-400')
                        .style('fill', 'currentColor')
                        .text(d.leftLabel || `${d.percentage}${this.showPercentage ? '%' : ''}`);
                }

                if (this.showRightLabel) {
                    if (this.rightAlignLabels) {
                        // Dynamically compute the X coordinate of the slanted edge at centerY
                        const xMidRight = (xTopRight + xBottomRight) / 2;

                        // Draw long dotted line to the far right extending into the margin
                        svg.append('line')
                            .attr('x1', xMidRight + (this.isContinuous ? 0 : 10))
                            .attr('y1', rightCenterY)
                            .attr('x2', chartWidth + 20) // Extend into margin space
                            .attr('y2', rightCenterY)
                            .attr('stroke', '#cbd5e1')
                            .attr('stroke-dasharray', '2,4')
                            .attr('stroke-width', 1.5);

                        // Draw a small colored dot
                        svg.append('circle')
                            .attr('cx', chartWidth + 30)
                            .attr('cy', rightCenterY)
                            .attr('r', 2.5)
                            .attr('fill', color);

                        // Right aligned label with colored text for values
                        const rightX = chartWidth + 40;

                        const labelText = svg.append('text')
                            .attr('x', rightX)
                            .attr('y', rightCenterY)
                            .attr('text-anchor', 'start')
                            .attr('dominant-baseline', 'middle')
                            .attr('class', 'text-[12.5px] font-medium');

                        // Append text parts to allow different colors
                        const labelParts = (d.rightLabel || `${d.label}: ${d.value}`).split(':');
                        if (labelParts.length > 1) {
                            labelText.append('tspan')
                                .attr('fill', '#64748b')
                                .text(labelParts[0] + ':');
                            labelText.append('tspan')
                                .attr('fill', color)
                                .text(' ' + labelParts[1].trim());
                        } else {
                            labelText.text(d.rightLabel || `${d.label}: ${d.value}`)
                                .attr('fill', '#64748b');
                        }

                    } else {
                        svg.append('line')
                            .attr('x1', chartWidth - lineOffset)
                            .attr('y1', centerY)
                            .attr('x2', xTopRight + lineEndOffset)
                            .attr('y2', centerY)
                            .attr('stroke', '#94a3b8')
                            .attr('stroke-dasharray', '3,3')
                            .attr('opacity', 0.5);

                        // Right Count - responsive positioning
                        const rightX = chartWidth + (isMobile ? 12 : 16);
                        svg.append('text')
                            .attr('x', rightX)
                            .attr('y', centerY)
                            .attr('text-anchor', 'start')
                            .attr('dominant-baseline', 'middle')
                            .attr('class', 'text-[11px] font-medium text-slate-500 dark:text-gray-400')
                            .style('fill', 'currentColor')
                            .text(d.rightLabel || `${d.value}`);
                    }
                }
            }
            // Internal Label
            if (this.showCenterLabels) {
                layerGroup.append('text')
                    .attr('class', 'inner-label')
                    .attr('x', centerX)
                    .attr('y', centerY)
                    .attr('text-anchor', 'middle')
                    .attr('dominant-baseline', 'middle')
                    .attr('fill', '#fff')
                    .style('pointer-events', 'none')
                    .style('font-weight', '700')
                    .style('font-size', '10px')
                    .style('text-transform', 'uppercase')
                    .style('letter-spacing', '0.05em')
                    .text(d.label);
            }
        });
    }
}
